/* eslint-disable max-params */
import queryString from 'query-string';
import { Base64 } from 'js-base64';
import { CsrfToken, Auth0Config, UserFlow, JWT } from '../types';
import { AidFrontendConfigCollection } from './feature-flags/dynamic-config';
import { AidFrontendDynamicConfig } from '../providers/DynamicConfigs';
import { getBooleanDynamicConfigValueOrDefault } from '../providers/common';
import { maybeSaveNativeHandoffTokenFromUrl } from './native-handoff-token';

export enum LoginType {
  Apple = 'appleLogin',
  Slack = 'slackLogin',
  Microsoft = 'microsoftLogin',
  GoogleButton = 'googleButton',
  GoogleOneTap = 'googleYolo',
  // What the confirmation page RECEIVES for a Google-button signup. (`GoogleButton`
  // is only SENT on initiate; for personal google accounts, it's transformed in
  // id-auth and returns as `GoogleLogin`)
  GoogleLogin = 'googleLogin',
  SSO = 'ssoLogin',
}

export type InHouseSocialLoginType = LoginType.Apple | LoginType.Slack | LoginType.Microsoft;

/**
 * Used when we want to ensure that the user picks a specific email address from the social login
 * provider. Using the OIDC standard login_hint parameter still gives the user the option to pick
 * other accounts either on purpose or accidentally.
 */
export interface SocialLoginRecoveryOptions {
  expectedEmail: string;
  recoveryToken: JWT;
}

// common

/**
 * Builds up an OIDC `state` parameter. The `state` parameter is supposed to be used _as_ a CSRF token, and so is guaranteed to
 * be provided as a parameter once the OIDC provider redirects the user's browser back to Atlassian account. This means we can
 * smuggle information through the OIDC flow by packing and unpacking this parameter at the appropriate time.
 */
export const buildState = ({
  location,
  csrfToken,
  source,
  loginType,
  anonymousId,
  userFlow,
  recoveryOptions,
}: {
  location: Location;
  csrfToken: CsrfToken;
  source: string;
  loginType: LoginType;
  anonymousId?: string;
  userFlow?: UserFlow;
  recoveryOptions?: SocialLoginRecoveryOptions;
}) =>
  Base64.encode(
    JSON.stringify({
      csrfToken,
      query: location.search || undefined,
      source,
      loginType,
      anonymousId,
      userFlow,
      expects: recoveryOptions?.expectedEmail,
      recovery: recoveryOptions?.recoveryToken,
    })
  );

// Google

export interface GoogleLoginRedirectParams {
  location: Location;
  source: string;
  recoveryOptions?: SocialLoginRecoveryOptions;
}

export const redirectToGoogleLogin = ({
  location,
  source,
  recoveryOptions,
}: GoogleLoginRedirectParams) => {
  const searchParams: Record<string, string> = queryString.parse(location.search);
  const filteredParams = Object.fromEntries(
    Object.entries(searchParams).filter(([key]) => key !== 'login_hint')
  );
  const { userFlow } = filteredParams;

  const params = {
    ...filteredParams,
    prompt: 'select_account',
    source,
    loginType: LoginType.GoogleButton,
    userFlow,
    recovery: recoveryOptions?.recoveryToken,
  };

  // Persist any native signup handoff token so it survives the round-trip to the
  // social provider and can be re-applied on the social confirmation page.
  maybeSaveNativeHandoffTokenFromUrl(location);

  location.assign(
    `${location.protocol}//${location.host}/login/initiate/google?` + queryString.stringify(params)
  );
};

// Auth0

export interface SocialLoginRedirectParams {
  location: Location;
  auth0Config?: Auth0Config;
  csrfToken: CsrfToken;
  source: string;
  email?: string;
  recoveryOptions?: SocialLoginRecoveryOptions;
}

const redirectToSocialLoginInitiate = ({
  location,
  source,
  recoveryOptions,
  loginType,
  loginHint,
}: {
  location: Location;
  source: string;
  recoveryOptions?: SocialLoginRecoveryOptions;
  loginType: LoginType.Microsoft | LoginType.Slack | LoginType.Apple;
  loginHint?: string;
}) => {
  const initiateParams = {
    ...queryString.parse(location.search),
    loginType,
    source,
    recovery: recoveryOptions?.recoveryToken,
    prompt: 'select_account',
    login_hint: loginHint,
  };

  let initiateUriSuffix;
  switch (loginType) {
    case LoginType.Apple:
      initiateUriSuffix = 'apple';
      break;
    case LoginType.Microsoft:
      initiateUriSuffix = 'microsoft';
      break;
    case LoginType.Slack:
      initiateUriSuffix = 'slack';
      break;
  }

  // Persist any native signup handoff token so it survives the round-trip to the
  // social provider and can be re-applied on the social confirmation page.
  maybeSaveNativeHandoffTokenFromUrl(location);

  location.assign(
    `${location.protocol}//${location.host}/login/initiate/${initiateUriSuffix}?` +
      queryString.stringify(initiateParams)
  );
};

// Microsoft

export const redirectToMicrosoftLogin = (params: SocialLoginRedirectParams) => {
  const { location, source, recoveryOptions, email } = params;
  redirectToSocialLoginInitiate({
    location,
    source,
    recoveryOptions,
    loginType: LoginType.Microsoft,
    loginHint: email,
  });
};

// Apple

export const redirectToAppleLogin = (params: SocialLoginRedirectParams) => {
  const { location, source, recoveryOptions, email } = params;
  redirectToSocialLoginInitiate({
    location,
    source,
    recoveryOptions,
    loginType: LoginType.Apple,
    loginHint: email,
  });
};

export const isAppleHiddenEmail = (email: string): boolean => {
  if (!email) {
    return false;
  }

  // Hidden email as defined by <unique-alphanumeric-string>@privaterelay.appleid.com
  const hiddenEmailDomainIdentifier = 'privaterelay.appleid.com';
  const [domain] = email.split('@').slice(-1);
  return domain === hiddenEmailDomainIdentifier;
};

// Slack

export const redirectToSlackLogin = (params: SocialLoginRedirectParams) => {
  const { location, source, recoveryOptions } = params;
  redirectToSocialLoginInitiate({
    location,
    source,
    recoveryOptions,
    loginType: LoginType.Slack,
    // Slack's OIDC implementation expects login_hint to be a JWT and blows up if it's not. If
    // Slack ever supports using an email as the login_hint (like other OIDC providers) then we
    // should remove this line.
    loginHint: undefined,
  });
};

export const isGoogleLoginOutageAdvisoryEnabled = (
  dynamicConfigs: AidFrontendConfigCollection
): boolean =>
  getBooleanDynamicConfigValueOrDefault(
    dynamicConfigs,
    AidFrontendDynamicConfig.GOOGLE_LOGIN_OUTAGE_ADVISORY
  );

export const isAppleLoginOutageAdvisoryEnabled = (
  dynamicConfigs: AidFrontendConfigCollection
): boolean =>
  getBooleanDynamicConfigValueOrDefault(
    dynamicConfigs,
    AidFrontendDynamicConfig.APPLE_LOGIN_OUTAGE_ADVISORY
  );

export const isMicrosoftLoginOutageAdvisoryEnabled = (
  dynamicConfigs: AidFrontendConfigCollection
): boolean =>
  getBooleanDynamicConfigValueOrDefault(
    dynamicConfigs,
    AidFrontendDynamicConfig.MICROSOFT_LOGIN_OUTAGE_ADVISORY
  );

export const isSlackLoginOutageAdvisoryEnabled = (
  dynamicConfigs: AidFrontendConfigCollection
): boolean =>
  getBooleanDynamicConfigValueOrDefault(
    dynamicConfigs,
    AidFrontendDynamicConfig.SLACK_LOGIN_OUTAGE_ADVISORY
  );
